import { Alert, Button, Form, Spin } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../../contexts/ToastContext";
import { getInsuranceCustomerUserId, getMissingInsuranceDetails } from "../../../lib/insuranceSaleApi";
import { searchUsers } from "../../../lib/teqMobilityCoverageApi";
import AddInsuranceCustomerModal from "../AddInsuranceCustomerModal";
import CustomerSearchList from "../shared/CustomerSearchList";

const SelectCustomerStep = ({
    availableProviders,
    provider,
    customer,
    onCustomerChange,
    customerLoading,
    parentForm,
    companyData,
}) => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [customerModalOpen, setCustomerModalOpen] = useState(false);
    const [customerModalMode, setCustomerModalMode] = useState("create");
    const debounceRef = useRef(null);

    const loadUsers = useCallback(async (searchTerm = "") => {
        setSearchLoading(true);
        try {
            const result = await searchUsers(searchTerm);
            setUsers(result);
        } catch (error) {
            setUsers([]);
            toast.error({
                message: t("wizard.loadCustomersError"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setSearchLoading(false);
        }
    }, [toast, t]);

    useEffect(() => {
        loadUsers("");
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [loadUsers]);

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => loadUsers(value), 300);
    };

    const openCustomerModal = (mode) => {
        setCustomerModalMode(mode);
        setCustomerModalOpen(true);
    };

    const handleCustomerSaved = async (savedUser) => {
        setCustomerModalOpen(false);

        const userId = getInsuranceCustomerUserId(savedUser);
        if (!userId) {
            return;
        }

        await loadUsers(searchQuery);
        parentForm?.setFieldValue("userId", userId);
        onCustomerChange(userId);
    };

    const handleSelectCustomer = (userId) => {
        parentForm?.setFieldValue("userId", userId || undefined);
        onCustomerChange(userId);
    };

    const missingDetails = getMissingInsuranceDetails(customer, provider, companyData);
    const selectedUserId = Form.useWatch("userId", parentForm);

    return (
        <div className="space-y-3">
            <Form.Item
                name="userId"
                rules={[{ required: true, message: t("wizard.customerRequired") }]}
                className="mb-0 hidden">
                <input type="hidden" />
            </Form.Item>

            {customer && selectedUserId && !customerLoading && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[#D9D9FB] bg-[#F3F3FE] px-4 py-3">
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-[#5D5DF6]">{t("wizard.selectedCustomer")}</p>
                        <p className="truncate text-sm font-semibold text-gray-900">
                            {[customer.firstName, customer.lastName].filter(Boolean).join(" ") || customer.email}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                            {[customer.email, customer.mobileNumber].filter(Boolean).join(" · ")}
                        </p>
                    </div>
                    <Button size="small" onClick={() => handleSelectCustomer(undefined)}>
                        {t("wizard.changeCustomer")}
                    </Button>
                </div>
            )}

            <CustomerSearchList
                availableProviders={availableProviders}
                users={users}
                loading={searchLoading || customerLoading}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                selectedUserId={selectedUserId}
                onSelect={handleSelectCustomer}
                onAddCustomer={() => openCustomerModal("create")}
            />

            {customerLoading && (
                <div className="flex justify-center py-2">
                    <Spin />
                </div>
            )}

            {customer && !customerLoading && missingDetails.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={t("wizard.customerIncompleteTitle")}
                    description={t("wizard.customerIncompleteDescription", {
                        fields: missingDetails.join(", "),
                    })}
                    action={
                        <Button
                            className="mt-2 w-full sm:mt-0 sm:w-auto"
                            onClick={() => openCustomerModal("edit")}>
                            {t("wizard.completeProfile")}
                        </Button>
                    }
                />
            )}

            <AddInsuranceCustomerModal
                open={customerModalOpen}
                mode={customerModalMode}
                provider={provider}
                initialUser={customerModalMode === "edit" ? customer : null}
                onClose={() => setCustomerModalOpen(false)}
                onSuccess={handleCustomerSaved}
            />
        </div>
    );
};

SelectCustomerStep.propTypes = {
    availableProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
    provider: PropTypes.string,
    customer: PropTypes.object,
    onCustomerChange: PropTypes.func.isRequired,
    customerLoading: PropTypes.bool,
    parentForm: PropTypes.object,
    companyData: PropTypes.object,
};

SelectCustomerStep.defaultProps = {
    provider: undefined,
    customer: null,
    customerLoading: false,
    parentForm: null,
    companyData: null,
};

export default SelectCustomerStep;
